import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { PlayerStatuses } from '../stores/AudioPlaybackStore';
import { logMediaError } from '../utils/logMediaError';
import throttle from 'transmute/throttle';
import { MediaPlaybackRateContext } from '../context/MediaPlaybackRateContext';
import { MediaPlaybackControlsContext } from '../context/MediaPlaybackControlsContext';
import { refreshRate } from '../../constants/MediaPlayerSettings';
import { MediaElementContext } from '../context/MediaElementContext';
import { createTimeout } from '../utils/createTimeout';
import { getParsedPlayerStatuses } from '../utils/getParsedPlayerStatuses';
import { MEDIA_BUFFER_STATUSES } from '../constants/MediaBufferStatus';
import { MediaPlaybackBufferContext } from '../context/MediaPlaybackBufferContext';
export const useHTMLMediaElementEvents = ({
  refreshRecordingUrl
} = {}) => {
  const {
    playbackRate
  } = useContext(MediaPlaybackRateContext);
  const {
    clipEndTime,
    clipStartTime,
    pause,
    playbackRequested,
    setPlayerHeadLocation,
    status
  } = useContext(MediaPlaybackControlsContext);
  const {
    mediaRef,
    recordingUrl,
    refetchedMediaUrl,
    setCurrentTime,
    setDuration,
    setErrorStatus,
    setRefetchedMediaUrl,
    setStatus,
    source
  } = useContext(MediaElementContext);
  const {
    setBufferStatus,
    setPercentLoaded
  } = useContext(MediaPlaybackBufferContext);
  const {
    isPlaying,
    isError,
    isLoading
  } = getParsedPlayerStatuses(status);
  const [isRefreshingRecordingUrl, setIsRefreshingRecordingUrl] = useState(null);
  const isStalledTimeout = useRef(undefined);
  const currentTime = useRef(0);
  useEffect(() => {
    var _isStalledTimeout$cur;
    (_isStalledTimeout$cur = isStalledTimeout.current) === null || _isStalledTimeout$cur === void 0 || _isStalledTimeout$cur.call(isStalledTimeout);
  }, [recordingUrl]);
  useEffect(() => () => {
    var _isStalledTimeout$cur2;
    (_isStalledTimeout$cur2 = isStalledTimeout.current) === null || _isStalledTimeout$cur2 === void 0 || _isStalledTimeout$cur2.call(isStalledTimeout);
  }, []);
  const setCurrentTimeCallback = useCallback(() => {
    var _isStalledTimeout$cur3;
    (_isStalledTimeout$cur3 = isStalledTimeout.current) === null || _isStalledTimeout$cur3 === void 0 || _isStalledTimeout$cur3.call(isStalledTimeout);
    if (mediaRef.current && typeof mediaRef.current.currentTime === 'number' && mediaRef.current.currentTime !== currentTime.current) {
      const oldCurrentTime = currentTime.current;
      const newCurrentTime = mediaRef.current.currentTime;

      // We can't just check for currentTime >= clipEndTime or user won't be able to play the rest of the clip
      // We only want to pause once at the moment the player goes from being before the clip end time to after it
      if (clipEndTime && clipEndTime > clipStartTime && oldCurrentTime < clipEndTime && newCurrentTime >= clipEndTime) {
        pause();
        setPlayerHeadLocation(clipEndTime);
      } else {
        setCurrentTime(newCurrentTime);
      }
      currentTime.current = newCurrentTime;
    }
  }, [mediaRef, clipEndTime, clipStartTime, pause, setPlayerHeadLocation, setCurrentTime]);
  return {
    onCanPlayThrough: useCallback(() => {
      if (isLoading) {
        setStatus(PlayerStatuses.INITIAL);
        currentTime.current = clipStartTime;
        if (mediaRef.current) {
          if (refetchedMediaUrl) {
            setRefetchedMediaUrl(false);
            setPlayerHeadLocation(clipStartTime);
          } else {
            setPlayerHeadLocation(clipStartTime);
          }
          mediaRef.current.playbackRate = playbackRate;
          if (playbackRequested) {
            mediaRef.current.play().catch(() => {});
          }
        }
      } else if (refetchedMediaUrl && isRefreshingRecordingUrl && !isError) {
        const {
          wasPlaying,
          lastCurrentTime
        } = isRefreshingRecordingUrl;
        setPlayerHeadLocation(lastCurrentTime || clipStartTime);
        if (mediaRef.current) {
          var _mediaRef$current;
          mediaRef.current.playbackRate = playbackRate;
          if (wasPlaying) (_mediaRef$current = mediaRef.current) === null || _mediaRef$current === void 0 || _mediaRef$current.play().catch(() => {});
        }
      }
      setIsRefreshingRecordingUrl(null);
      setRefetchedMediaUrl(false);
    }, [clipStartTime, isError, isLoading, isRefreshingRecordingUrl, mediaRef, playbackRate, playbackRequested, refetchedMediaUrl, setPlayerHeadLocation, setRefetchedMediaUrl, setStatus]),
    onEnded: useCallback(() => {
      if (!isError) {
        setStatus(PlayerStatuses.PAUSED);
        setPlayerHeadLocation(0);
      }
    }, [setStatus, setPlayerHeadLocation, isError]),
    onError: useCallback(() => {
      if (refreshRecordingUrl && !isError) {
        var _mediaRef$current$cur, _mediaRef$current2;
        setIsRefreshingRecordingUrl({
          wasPlaying: isPlaying,
          lastCurrentTime: (_mediaRef$current$cur = (_mediaRef$current2 = mediaRef.current) === null || _mediaRef$current2 === void 0 ? void 0 : _mediaRef$current2.currentTime) !== null && _mediaRef$current$cur !== void 0 ? _mediaRef$current$cur : currentTime.current
        });
        refreshRecordingUrl().catch(() => {});
        return;
      }
      logMediaError({
        mediaElement: mediaRef.current,
        requestedRecordingUrlUpdate: !!isRefreshingRecordingUrl,
        setErrorStatus,
        source
      });
      setRefetchedMediaUrl(false);
      setIsRefreshingRecordingUrl(null);
    }, [isRefreshingRecordingUrl, mediaRef, refreshRecordingUrl, setErrorStatus, setRefetchedMediaUrl, source, isPlaying, isError]),
    onProgress: useCallback(e => {
      try {
        var _isStalledTimeout$cur4;
        if (e.currentTarget.readyState !== 4 || e.currentTarget.buffered.length === 0) {
          return;
        }
        const bufferedAmount = Math.floor(e.currentTarget.buffered.end(e.currentTarget.buffered.length - 1));
        const percentLoaded = bufferedAmount / e.currentTarget.duration * 100;
        setPercentLoaded(Math.round(percentLoaded >= 99 ? 100 : percentLoaded));
        (_isStalledTimeout$cur4 = isStalledTimeout.current) === null || _isStalledTimeout$cur4 === void 0 || _isStalledTimeout$cur4.call(isStalledTimeout);
      } catch (error) {
        // If the media element is not loaded for some reason, the buffered property will not exist
        // This is a known issue with the HTMLMediaElement
        // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/buffered
        // We can safely ignore this error
      }
    }, [setPercentLoaded]),
    onLoadedMetadata: useCallback(val => {
      const realDuration = val.currentTarget.duration;
      if (!isNaN(realDuration) && isFinite(realDuration)) {
        setDuration(realDuration);
      }
    }, [setDuration]),
    onPause: useCallback(() => {
      if (!isError) {
        setStatus(PlayerStatuses.PAUSED);
      }
    }, [setStatus, isError]),
    onPlaying: useCallback(() => {
      if (!isError) {
        setStatus(PlayerStatuses.PLAYING);
      }
    }, [setStatus, isError]),
    onTimeUpdate: throttle(clipEndTime ? refreshRate : 1000, setCurrentTimeCallback),
    onStalled: useCallback(() => {
      if (!isError) {
        var _isStalledTimeout$cur5;
        (_isStalledTimeout$cur5 = isStalledTimeout.current) === null || _isStalledTimeout$cur5 === void 0 || _isStalledTimeout$cur5.call(isStalledTimeout);
        // IF within 2 seconds of the last stalled event no clearing event occurs, set the status to stalled
        isStalledTimeout.current = createTimeout(() => {
          setBufferStatus(MEDIA_BUFFER_STATUSES.BUFFERING);
        }, 2000);
      }
    }, [isError, setBufferStatus])
  };
};